import { useState, useCallback } from "react";
import { Control, UseFormSetValue, UseFormUnregister } from "react-hook-form";
import {
  RelatorioPrestacaoContasForm,
  RelatorioPrestacaoContasItem,
  TipoItemRelatorioPrestacaoContas,
} from "core/features/relatorios/prestacao-contas/typings";
import {
  RelatorioIdEnum,
  GrupoRelatorioIdEnum,
  FieldRelatorioColumn,
} from "core/features/relatorios/typings";
import { Row, Col } from "antd";
import { Box, Icon, Flex, Text } from "@chakra-ui/react";
import Field from "components/Input/Field";
import SelectTipoItemConfiguracaoRelatorio from "./SelectTipoItemConfiguracaoRelatorio";
import SelectRelatorioTree from "components/Input/SelectRelatorioTree";
import AddRemoveLineButton from "components/Button/AddRemoveLineButton";
import { FaCog, FaEye } from "react-icons/fa";
import colors from "resources/theme/colors";
import { TipoFiltroDataRelatorioPrestacaoContas } from "features/relatorios/prestacao-contas/typings";
import ModalConfiguracaoAdicional from "./ModalConfiguracaoAdicional";
import getColunasFormConfiguracaoAdicional from "./../util/getColunasFormConfiguracaoAdicional";
import FiltrosAdicionais from "./FiltrosAdicionais";
import { SortableDragHandle } from "components/SortableDragHandle";
import ButtonAnexarArquivosTemp from "components/Button/ButtonAnexarArquivosTemp";
import { ListaRelatoriosResponse } from "core/features/relatorios/typings";
import { useEffect } from "react";
export interface ItemConfiguracaoProps {
  itemIndex: number;
  item: Partial<RelatorioPrestacaoContasItem>;
  control: Control<RelatorioPrestacaoContasForm, any>;
  setValue: UseFormSetValue<RelatorioPrestacaoContasForm>;
  unregister?: UseFormUnregister<RelatorioPrestacaoContasForm>;
  onRemove?: () => any;
  draggable?: boolean;
  erroConfiguracao?: boolean;
  onVisualize?: (item: Partial<RelatorioPrestacaoContasItem>) => any;
}

function ItemConfiguracao({
  itemIndex,
  item,
  control,
  setValue,
  onRemove,
  draggable = false,
  erroConfiguracao,
  onVisualize,
  unregister,
}: ItemConfiguracaoProps) {
  const [openModalConfigAdicional, setOpenModalConfigAdicional] =
    useState(false);

  const isCapa = item.tipoItem === TipoItemRelatorioPrestacaoContas.Capa;
  const isRelatorio =
    item.tipoItem === TipoItemRelatorioPrestacaoContas.Relatorio;
  const isPdfExterno =
    item.tipoItem === TipoItemRelatorioPrestacaoContas.PdfExterno;

  useEffect(() => {
    if (item.relatorioId === RelatorioIdEnum.ComposicaoReceitasHorizontal) {
      unregister?.(`items.${itemIndex}.parametros.filtrosAdicionais.pago`);
    }

    if (item.relatorioId !== RelatorioIdEnum.TotalDeDespesas) {
      unregister?.(`items.${itemIndex}.parametros.filtrosAdicionais.pessoaId`);
      unregister?.(
        `items.${itemIndex}.parametros.filtrosAdicionais.rateioFechamentoId`
      );
    }

    if (item.relatorioId !== RelatorioIdEnum.Inadimplentes) {
      unregister?.(
        `items.${itemIndex}.parametros.filtrosAdicionais.porcentagemHonorarios`
      );
      unregister?.(`items.${itemIndex}.parametros.filtrosAdicionais.cpf`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.relatorioId, unregister]);

  const onChangeRelatorioId = useCallback(
    (relatorioId: RelatorioIdEnum, pdfUrl?: string | null) => {
      if (relatorioId === RelatorioIdEnum.DemonstrativoAnual) {
        setValue(
          `items.${itemIndex}.tipoFiltroData`,
          TipoFiltroDataRelatorioPrestacaoContas.AnoAtual
        );
      }

      setValue(`items.${itemIndex}.relatorioPdfUrl`, pdfUrl);

      const fieldsOptions =
        getColunasFormConfiguracaoAdicional(relatorioId) ?? [];

      const items = fieldsOptions.reduce(
        (acc, next) => acc.concat(next.items),
        [] as FieldRelatorioColumn[]
      );

      setValue(
        `items.${itemIndex}.parametros.colunas`,
        items
          .filter((item) => item.defaultChecked !== false)
          .map((item) => item.value)
      );
    },
    [itemIndex, setValue]
  );

  const showVisualizeItem = () => {
    if (!onVisualize) return false;

    if (isPdfExterno && !item.arquivo) return false;

    if (isRelatorio && !item.relatorioId) return false;

    return true;
  };

  return (
    <Flex flexDir="column">
      <Box as={Row} gutter={16}>
        <Box as={Col} xs={8}>
          <Flex gridGap={1}>
            {draggable ? (
              <Flex
                bg={colors.black700}
                roundedLeft={6}
                h="40px"
                mt={5}
                alignItems="center"
              >
                <SortableDragHandle size={16} color="white" />
              </Flex>
            ) : null}

            <Field
              control={control}
              name={`items.${itemIndex}.tipoItem`}
              label="Tipo de conteúdo"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <SelectTipoItemConfiguracaoRelatorio
                  {...field}
                  allowClear={false}
                />
              )}
            />
          </Flex>
        </Box>
        <Box as={Col} xs={12}>
          {isCapa ? (
            <Field
              control={control}
              name={`items.${itemIndex}.titulo`}
              shouldUnregister
              label="Título da capa"
              inputProps={{
                maxLength: 255,
              }}
            />
          ) : null}

          {isRelatorio ? (
            <Field
              control={control}
              name={`items.${itemIndex}.relatorioId`}
              shouldUnregister={!isRelatorio}
              label="Relatório"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <SelectRelatorioTree
                  params={{
                    grupoRelatoriosIds: [GrupoRelatorioIdEnum.Financeiro],
                  }}
                  value={item.relatorioId}
                  allowClear={false}
                  onChange={(val, _, extra) => {
                    const triggerNode = extra.triggerNode
                      ?.props as ListaRelatoriosResponse;

                    onChangeRelatorioId(val, triggerNode?.pdfUrl);
                    field.onChange(val);
                  }}
                />
              )}
            />
          ) : null}

          {isPdfExterno ? (
            <Field
              control={control}
              name={`items.${itemIndex}.arquivo`}
              shouldUnregister
              label="Arquivo"
              render={({ field }) => (
                <ButtonAnexarArquivosTemp
                  horizontalAlignment
                  accept="application/pdf"
                  maxFileSizeMb={200}
                  fileName={field.value?.nome}
                  currentFilesLength={field.value ? 1 : 0}
                  multiple={false}
                  buttonText="Arquivo"
                  onFinishUploading={(arquivos) => {
                    const [arquivo] = arquivos;

                    field.onChange(arquivo);
                    setValue(`items.${itemIndex}.arquivoId`, arquivo.id);
                  }}
                />
              )}
            />
          ) : null}
        </Box>
        <Box as={Col} xs={4}>
          <Flex gridGap={2}>
            {!isPdfExterno ? (
              <Icon
                mt={6}
                as={FaCog}
                w={8}
                h={8}
                color={colors.darkGray}
                onClick={() => setOpenModalConfigAdicional(true)}
                cursor="pointer"
              />
            ) : null}

            {showVisualizeItem() ? (
              <Icon
                mt={6}
                as={FaEye}
                w={8}
                h={8}
                color={colors.darkGray}
                onClick={() => onVisualize?.(item)}
                cursor="pointer"
              />
            ) : null}

            {onRemove ? (
              <AddRemoveLineButton
                showAdd={false}
                onRemove={onRemove}
                containerProps={{ pt: 6 }}
              />
            ) : null}
          </Flex>
        </Box>
      </Box>

      {erroConfiguracao ? (
        <Text mt={2} fontWeight="500" color={colors.red500}>
          Erro na configuração do item
        </Text>
      ) : null}

      <ModalConfiguracaoAdicional
        isOpen={openModalConfigAdicional}
        onClose={() => setOpenModalConfigAdicional(false)}
        control={control}
        index={itemIndex}
        setValue={setValue}
        item={item}
        colunas={getColunasFormConfiguracaoAdicional(item.relatorioId)}
        ComponentFiltrosAdicionais={
          isRelatorio ? (
            <FiltrosAdicionais
              control={control}
              index={itemIndex}
              item={item}
              setValue={setValue}
            />
          ) : null
        }
      />
    </Flex>
  );
}

export default ItemConfiguracao;
